import { useLDClient } from 'launchdarkly-react-client-sdk'
import { useEffect } from 'react'

import { useAppexRedesignFlag } from '../featureFlags'
import {
    addRumFeatureFlagEvaluation,
    setRumGlobalContextProperty,
} from '../utils/datadogRum'

import { useAuth } from './AuthContext'

/**
 * Enriches Datadog RUM with rollout-observability context. Renders nothing;
 * every call is a no-op when RUM is disabled.
 *
 * - `appexRedesign` global context property + feature flag evaluation, so the
 *   redesign rollout can be segmented in RUM (only reported once the
 *   LaunchDarkly client exists, to avoid tagging sessions with the code
 *   default before flags resolve).
 * - `storeHash` global context property (BigCommerce store identifier, not
 *   PII), so sessions can be correlated with a store's rollout cohort.
 */
export function DatadogRumTracker(): null {
    const ldClient = useLDClient()
    const { platform } = useAuth()
    const redesignEnabled = useAppexRedesignFlag()

    useEffect(() => {
        if (!ldClient) {
            return
        }
        setRumGlobalContextProperty('appexRedesign', redesignEnabled)
        addRumFeatureFlagEvaluation('appex-redesign', redesignEnabled)
    }, [ldClient, redesignEnabled])

    useEffect(() => {
        const storeHash = platform?.platform_id ?? platform?.id
        if (storeHash) {
            setRumGlobalContextProperty('storeHash', String(storeHash))
        }
    }, [platform])

    return null
}
